"use client";

import {
  Box,
  SxProps,
  Theme,
  TextField,
  Typography,
  FormControlLabel,
  Checkbox,
  Link as MuiLink,
  Divider,
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  emailInput,
  label,
  loginForm,
  passwordInput,
  submitButton,
} from "./classes";
import { GoogleLoginButton } from "@yardzen/components/google-login-button";
import { YardzenButton } from "@yardzen/components/yardzen-button";
import Link from "next/link";
import {
  SegmentForms,
  hasLowerCaseLetterRegExp,
  hasMinimum6CharsRegExp,
  hasNumberRegExp,
  hasSpecialCharRegExp,
  hasUpperCaseLetterRegExp,
  passwordRequirements,
} from "@yardzen/next-client-util";
import { analytics } from "@yardzen/next-segment";
import { useCallback, useEffect, useState } from "react";
import { isEmail } from "class-validator";

export interface SubmissionError {
  field: string;
  message: string;
  action: "try again" | "login";
}

interface Props {
  buttonText: string;
  submittingText: string;
  onSubmit: (values: any) => Promise<void | string>;
  enableGoogleLogin?: boolean;
  checkLoginProviders?: (email: string) => Promise<string[]>;

  // Shows or hides the password field, this should be set to false if this is a password reset
  // request form
  showPassword?: boolean;
  showSignupConsent?: boolean;
  buttonDisabled?: boolean;
  showPasswordRequirements?: boolean;
  submissionErrors?: SubmissionError[];
  flowName: string;
  subFlowName?: string;
  setSubmissionErrors?: (errors: SubmissionError[]) => void;
  sx?: SxProps<Theme>;

  // Set to true if this form is used for signing in, as opposed to signing up or requesting a
  // password reset
  isSignInForm?: boolean;

  defaultEmail?: string | undefined | null;
  segmentFormName?: SegmentForms;
  tosChecked?: boolean;
  setTosChecked?: (checked: boolean) => void;
  showForgotPassword?: boolean;
}

const validationSchema = (isSignInForm: boolean, showPassword: boolean) => {
  if (isSignInForm && showPassword) {
    // Do not run the normal password regex on the password entry screen
    // but still require a password. The reason this regex is toned down is because
    // the reset password screeen (For now) does not have the same password requirements
    // and we do not want to block sign ib
    // TODO: fix this ^^
    return Yup.object({
      email: Yup.string()
        .email("Invalid email address")
        .required("Please enter your email"),
      password: Yup.string().required("Please enter your password"),
    });
  } else if (!showPassword) {
    // if show password is set to false that implies this is a password reset request form
    // that only shows the email, so set the password to not required
    return Yup.object({
      email: Yup.string()
        .email("Invalid email address")
        .required("Please enter your email"),
      password: Yup.string().optional(),
    });
  }

  // This is a sign up form
  return Yup.object({
    email: Yup.string()
      .email("Invalid email address")
      .required("Please enter your email"),
    password: Yup.string()
      .required("Please enter your password")
      .matches(hasNumberRegExp, "Password must contain at least one number")
      .matches(
        hasLowerCaseLetterRegExp,
        "Password must contain at least one lowercase letter",
      )
      .matches(
        hasUpperCaseLetterRegExp,
        "Password must contain at least one uppercase letter",
      )
      .matches(
        hasSpecialCharRegExp,
        "Password must contain at least one special character",
      )
      .matches(
        hasMinimum6CharsRegExp,
        "Password must contain at least 6 characters",
      ),
  });
};

export function LoginForm({
  buttonDisabled = false,
  buttonText,
  isSignInForm = false,
  onSubmit,
  setSubmissionErrors,
  showPassword = true,
  showPasswordRequirements = false,
  showSignupConsent = false,
  submissionErrors,
  submittingText,
  defaultEmail = "",
  sx,
  segmentFormName,
  flowName,
  subFlowName,
  tosChecked,
  checkLoginProviders,
  setTosChecked,
  enableGoogleLogin,
  showForgotPassword = false,
}: Props) {
  const {
    values,
    handleChange,
    handleBlur,
    touched,
    handleSubmit,
    errors,
    isSubmitting,
  } = useFormik({
    initialValues: { email: defaultEmail, password: "" },
    validationSchema: validationSchema(isSignInForm, showPassword),
    onSubmit: async (values) => {
      await onSubmit(values);
    },
  });

  const [loginProviders, setLoginProviders] = useState<Set<string> | null>(
    null,
  );

  const requiresGoogleLogin =
    loginProviders?.has?.("google.com") && !loginProviders?.has?.("password");

  console.log("requiresGoogleLogin", requiresGoogleLogin, [
    ...(loginProviders || []),
  ]);

  const handleCheckLoginProviders = useCallback(
    async (email: string | null) => {
      if (email && isEmail(email) && isSignInForm && checkLoginProviders) {
        try {
          const providers = await checkLoginProviders(email);

          setLoginProviders(new Set(providers));
        } catch (error) {
          console.error("error fetching login providers", error);
          setLoginProviders(null);
        }
      } else {
        setLoginProviders(null);
      }
    },
    [values.email, checkLoginProviders],
  );

  useEffect(() => {
    const { email } = values;
    const timeout = setTimeout(async () => {
      await handleCheckLoginProviders(email);
    }, 500);

    return () => clearTimeout(timeout);
  }, [values.email]);

  const supportLink = (
    <Link href="mailto:hello@yardzen.com" style={{ color: "#c05757" }}>
      Support
    </Link>
  );

  const loginLink = (
    <Link href="/login" style={{ color: "#c05757" }}>
      Log in
    </Link>
  );
  const consentLabelText = (
    <Typography
      sx={{
        fontSize: "0.625rem",
        lineHeight: "0.9375rem",
        letterSpacing: "0.03125rem",
      }}
    >
      By creating an account, you agree to Yardzen's{" "}
      <Link
        href="https://yardzen.com/electronic-signature-disclosures"
        target="_blank"
        className="text-blue-700 underline"
      >
        Consent to Electronic Signature and Disclosures,
      </Link>{" "}
      <Link
        href="https://yardzen.com/terms-and-conditions"
        target="_blank"
        className="text-blue-700 underline"
      >
        Terms of Service
      </Link>{" "}
      and{" "}
      <Link
        href="https://yardzen.com/privacy-policy"
        target="_blank"
        className="text-blue-700 underline"
      >
        Privacy Policy
      </Link>
    </Typography>
  );

  const shouldDisableSubmit =
    isSubmitting || buttonDisabled || (showSignupConsent && !tosChecked);

  const emailSubmissionError = submissionErrors?.find(
    ({ field }) => field === "email",
  );

  const passwordPlaceholder = isSignInForm
    ? "Enter your password"
    : "Create a password";

  return (
    <Box sx={[...(Array.isArray(sx) ? sx : [sx])]}>
      <Box component="form" onSubmit={handleSubmit} sx={loginForm}>
        <Typography variant="body1" sx={label}>
          Email
        </Typography>
        <TextField
          hiddenLabel
          name="email"
          onChange={(e) => {
            handleChange(e);
            if (submissionErrors && setSubmissionErrors) {
              setSubmissionErrors([
                ...submissionErrors.filter(({ field }) => field !== "email"),
              ]);
            }
          }}
          onBlur={(e) => {
            handleBlur(e);
            if (segmentFormName && e.target.value) {
              analytics.trackFormFieldFilled({
                field_name: "email",
                form_name: segmentFormName,
                text_value: e.target.value,
                input_type: "text",
              });
            }
          }}
          aria-label="enter your email"
          aria-required="true"
          sx={emailInput}
          variant="standard"
          value={values.email}
          type="text"
          error={touched.email && Boolean(errors.email)}
          placeholder="Enter your email address"
        />
        {emailSubmissionError && (
          <Typography
            sx={{
              width: "100%",
              color: "prosTheme.error.main",
              fontSize: "0.875rem",
            }}
          >
            {emailSubmissionError.message}{" "}
            {emailSubmissionError?.action === "try again"
              ? "Try again"
              : loginLink}{" "}
            or contact {supportLink}
          </Typography>
        )}
        {touched.email && errors.email && (
          <Typography
            sx={{
              width: "100%",
              color: "prosTheme.error.main",
              fontSize: "0.875rem",
            }}
          >
            {errors.email}. Try again or contact {supportLink}
          </Typography>
        )}
        {showPassword && (
          <>
            <Typography variant="body1" sx={{ ...label, marginTop: "1.88rem" }}>
              Password
            </Typography>
            <TextField
              hiddenLabel
              name="password"
              disabled={requiresGoogleLogin}
              helperText={
                requiresGoogleLogin ? "Your account uses Google sign in" : null
              }
              onChange={handleChange}
              onBlur={handleBlur}
              aria-label="password"
              aria-required="true"
              sx={passwordInput}
              value={values.password}
              variant="standard"
              type="password"
              error={touched.password && Boolean(errors.password)}
              placeholder={passwordPlaceholder}
            />
            {touched.password &&
              errors.password &&
              showPasswordRequirements && (
                <Box sx={{ width: "100%", marginTop: "0.5rem" }}>
                  <Typography>Your password must have:</Typography>
                  <ul>
                    {passwordRequirements(values.password).map((req) => (
                      <li key={req.text}>
                        <Typography
                          sx={{
                            color: req.requirement
                              ? "prosTheme.success.dark"
                              : "prosTheme.error.dark",
                          }}
                          variant="body2"
                        >
                          {req.text}{" "}
                          {req.element && (
                            <span>({<em>-+_!@#$%^&*.,?</em>})</span>
                          )}
                        </Typography>
                      </li>
                    ))}
                  </ul>
                </Box>
              )}
          </>
        )}
        {showSignupConsent && (
          <Box sx={{ marginTop: "1.87rem" }}>
            <FormControlLabel
              control={
                <Checkbox
                  value={tosChecked}
                  onChange={(e) =>
                    setTosChecked ? setTosChecked(e.target.checked) : null
                  }
                  sx={{
                    "&.Mui-checked": {
                      color: "prosTheme.text.secondary",
                    },
                  }}
                />
              }
              label={consentLabelText}
            />
          </Box>
        )}
        <Box sx={{ width: "100%" }}>
          {showForgotPassword && (
            <MuiLink href="/forgot-password">
              <Typography
                variant="body1"
                sx={{
                  fontSize: "0.875rem",
                  color: "prosTheme.text.secondary",
                  textDecorationColor: "inherit",
                  textDecoration: "underline",
                  marginTop: "1rem",
                  textAlign: "center",
                  mb: "1rem",
                }}
              >
                Forgot Password?
              </Typography>
            </MuiLink>
          )}
          <YardzenButton
            type="submit"
            variant="contained"
            color="primary"
            disabled={shouldDisableSubmit}
            sx={{
              ...submitButton,
              marginTop: showForgotPassword ? "0" : "2.5rem",
            }}
            onSubmit={() => {}}
            loading={isSubmitting}
          >
            {isSubmitting ? submittingText : buttonText}
          </YardzenButton>
        </Box>
        {enableGoogleLogin && (
          <div className="flex w-full flex-col items-center">
            <div className="flex w-full flex-row flex-nowrap items-center py-2">
              <span className="inline-block flex-grow">
                <Divider />
              </span>
              <div className="px-2">
                <span
                  className="font-display text-sm text-[#C4C4C4]"
                  style={{
                    color: "#C4C4C4",
                    fontSize: "12px",
                    letterSpacing: "2px",
                  }}
                >
                  OR
                </span>
              </div>
              <span className="inline-block  flex-grow">
                <Divider />
              </span>
            </div>
            <GoogleLoginButton
              trackingData={{
                flow_name: flowName,
                sub_flow_name: subFlowName,
              }}
              onClick={() => onSubmit("google")}
              variant={isSignInForm ? "login" : "signup"}
            />
          </div>
        )}
      </Box>
    </Box>
  );
}
